import React, { useCallback } from 'react';

import { Image, ImageBackground, ImageProps } from 'expo-image';
import { Text, View } from 'react-native';

import { useAppTakeover } from 'components/app-takeover/use-app-takeover';
import Link from 'components/link';
import Picture from 'components/picture';
import { useFormatCalories } from 'hooks/use-format-calories';
import { IMenuSectionItemProps } from 'pages/menu/components/menu-section/types';
import { buildMenuLink } from 'pages/menu/utils/navigation';
import { traceFirstMenuItemLoaded } from 'pages/menu/utils/performance';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants/primitives';
import { TraceName } from 'utils/performance';
import { startTrace } from 'utils/performance/trace';

import { menuSectionStyles } from './styled';

// NEW one menu
const _MenuSectionListItem: React.FC<IMenuSectionItemProps> = ({ item }) => {
  const { formatCurrencyForLocale } = useUIContext();
  const formatCalories = useFormatCalories();

  const { menu: menuAppTakeover, pdp: pdpAppTakeover, fontFamily } = useAppTakeover();
  const takeoverSection = menuAppTakeover?.targetSectionIds?.[item.id];
  const isTakeoverItem = !!pdpAppTakeover?.targetMenuItemIds?.[item.id];

  const linkPath = buildMenuLink(item);

  const handleLinkClick = useCallback(() => {
    const isProduct = !!item.product;
    const attributes = {
      component: isProduct
        ? ClickEventComponentNames.PRODUCTS
        : ClickEventComponentNames.PRODUCT_SECTION_TILES,
      name: item.name,
      componentId: item.id,
    };
    logButtonClick({ attributes }, { logDuplicateClickEvent: true });

    startTrace(isProduct ? TraceName.MENU_PDP_LOAD : TraceName.MENU_SECTION_LOAD, {
      menuItemId: item.id,
    });
  }, [item]);

  const onImageDisplay = useCallback(() => {
    traceFirstMenuItemLoaded({ menuItemId: item.id, isOneMenu: 'true' });
  }, [item.id]);

  const Element = takeoverSection ? ImageBackground : View;

  return (
    <Link key={item.id} onPress={handleLinkClick} linkPath={linkPath} dd-action-name={item.name}>
      <Element
        source={takeoverSection?.sectionBackgroundImage}
        style={[
          menuSectionStyles.menuSectionRow,
          {
            backgroundColor: isTakeoverItem ? undefined : primitive.$white,
          },
        ]}
        testID="menu-tile-bk"
      >
        <View style={menuSectionStyles.menuSectionImageWrapper}>
          <Picture
            image={item.image.resource}
            alt={item.image.description}
            onDisplay={onImageDisplay}
            accessibilityHint={t('select')}
            style={menuSectionStyles.menuSectionImage}
          />
        </View>

        <View style={menuSectionStyles.menuSectionContent}>
          <Text
            accessibilityRole="header"
            accessibilityLevel={2}
            style={[menuSectionStyles.menuSectionTitle, isTakeoverItem && { fontFamily }]}
            testID={`tile-header-${item.name?.toLowerCase().replace(/\s/g, '')}`}
            numberOfLines={2}
          >
            {item.name}
          </Text>
          {item.product && (
            <View style={menuSectionStyles.menuSectionProductWrapper}>
              <Text style={menuSectionStyles.menuSectionItemPrice}>
                {formatCurrencyForLocale(item.product.price)}
              </Text>
              <Text style={menuSectionStyles.menuSectionItemCalories} numberOfLines={1}>
                {formatCalories(item.product.calories)}
              </Text>
            </View>
          )}
        </View>
      </Element>
      {takeoverSection?.sectionImage && (
        <Image
          pointerEvents="box-none"
          source={takeoverSection?.sectionImage}
          style={[takeoverSection?.sectionImageStyle as ImageProps['style']]}
        />
      )}
    </Link>
  );
};

export const MenuSectionListItem = React.memo(_MenuSectionListItem);
